import { Link } from "react-router-dom";
import { Twitter, Youtube, Instagram, Mail } from "lucide-react";
import { useLeadModal } from "../context/LeadModalContext";

const LOGO_URL =
    "https://customer-assets.emergentagent.com/job_ez-agent-builder/artifacts/03sa3sjt_3224.png";

export default function Footer() {
    const { openModal } = useLeadModal();
    return (
        <footer
            data-testid="site-footer"
            className="relative bg-[#02040a] border-t border-white/5 overflow-hidden"
        >
            <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[60%] h-px bg-gradient-to-r from-transparent via-[#00f0ff]/40 to-transparent" />

            <div className="relative max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 pt-20 pb-10">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-16">
                    <div className="md:col-span-5">
                        <Link to="/" className="flex items-center gap-2.5 mb-5">
                            <div className="w-10 h-10 rounded-lg overflow-hidden ring-1 ring-[#00f0ff]/30">
                                <img
                                    src={LOGO_URL}
                                    alt="ezBuildABot logo"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                            <span className="font-heading font-extrabold text-lg">
                                <span className="text-[#00f0ff]">
                                    ez
                                </span>
                                <span className="text-white">buildabot</span>
                            </span>
                        </Link>
                        <p className="text-[#94a3b8] max-w-sm leading-relaxed">
                            Custom AI agents and apps for businesses that ship.
                        </p>

                        <div className="mt-6 space-y-2">
                            <a
                                href="mailto:info@ezbuildabot.com"
                                data-testid="footer-email"
                                className="inline-flex items-center gap-2 text-sm text-[#94a3b8] hover:text-[#00f0ff] transition-colors"
                            >
                                <Mail className="w-4 h-4" />
                                info@ezbuildabot.com
                            </a>
                            <div>
                                <button
                                    onClick={() => openModal("footer-book-call")}
                                    data-testid="footer-book-call"
                                    className="text-sm font-medium text-[#00f0ff] hover:text-white transition-colors"
                                >
                                    Book a call →
                                </button>
                            </div>
                        </div>

                        <div className="flex items-center gap-3 mt-6">
                            {[
                                { Icon: Instagram, label: "instagram", url: null },
                                { Icon: Youtube, label: "youtube", url: null },
                                { Icon: Twitter, label: "x", url: null },
                            ].map(({ Icon, label, url }) =>
                                url ? (
                                    <a
                                        key={label}
                                        href={url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid={`footer-social-${label}`}
                                        className="w-9 h-9 rounded-lg border border-white/10 flex items-center justify-center text-[#94a3b8] hover:text-[#00f0ff] hover:border-[#00f0ff]/40 transition-colors"
                                        aria-label={label}
                                    >
                                        <Icon className="w-4 h-4" />
                                    </a>
                                ) : (
                                    <span
                                        key={label}
                                        data-testid={`footer-social-${label}`}
                                        title="Coming soon"
                                        aria-label={`${label} (coming soon)`}
                                        className="w-9 h-9 rounded-lg border border-white/10 flex items-center justify-center text-[#94a3b8]/70 cursor-default"
                                    >
                                        <Icon className="w-4 h-4" />
                                    </span>
                                )
                            )}
                        </div>
                    </div>

                    <div className="md:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-8">
                        <FooterCol
                            title="Product"
                            links={[
                                { label: "AI Agents", to: "/#services" },
                                { label: "Web Apps", to: "/#services" },
                                { label: "Landing Pages", to: "/#services" },
                                { label: "Pricing", to: "/pricing" },
                            ]}
                        />
                        <FooterCol
                            title="Company"
                            links={[
                                { label: "Process", to: "/#process" },
                                { label: "Case Studies", to: "/#showcase" },
                                { label: "Contact", to: "/contact" },
                            ]}
                        />
                        <FooterCol
                            title="Legal"
                            links={[
                                { label: "Privacy Policy", to: "/privacy" },
                                { label: "Terms & Conditions", to: "/terms" },
                            ]}
                        />
                    </div>
                </div>

                <div className="relative">
                    <h2
                        aria-hidden
                        className="font-heading font-black select-none leading-none text-[14vw] md:text-[12vw] tracking-tighter text-transparent bg-clip-text bg-gradient-to-b from-white/[0.07] to-transparent"
                    >
                        EZBUILDABOT
                    </h2>
                </div>

                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-8 border-t border-white/5">
                    <p className="text-xs text-[#64748b]">
                        © {new Date().getFullYear()} ezBuildabot — a service of Gadget Guy Media. All rights reserved.
                    </p>
                    <div className="flex items-center gap-6 text-xs text-[#64748b]">
                        <Link to="/privacy" data-testid="footer-privacy" className="hover:text-white transition-colors">
                            Privacy
                        </Link>
                        <Link to="/terms" data-testid="footer-terms" className="hover:text-white transition-colors">
                            Terms
                        </Link>
                    </div>
                </div>

                <div className="pt-4 text-center sm:text-left">
                    <p className="text-xs text-[#64748b]">
                        Website Designed and Built by{" "}
                        <a
                            href="https://gadgetguymedia.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="footer-built-by"
                            className="text-[#94a3b8] hover:text-[#00f0ff] transition-colors font-medium"
                        >
                            Gadget Guy Media
                        </a>
                    </p>
                </div>
            </div>
        </footer>
    );
}

function FooterCol({ title, links }) {
    return (
        <div>
            <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-[#00f0ff] mb-4">
                {title}
            </h4>
            <ul className="space-y-3">
                {links.map((l) => (
                    <li key={l.label}>
                        <Link
                            to={l.to}
                            className="text-sm text-[#94a3b8] hover:text-white transition-colors"
                        >
                            {l.label}
                        </Link>
                    </li>
                ))}
            </ul>
        </div>
    );
}
